import { ArrowLeft, Bell, ChevronDown, Edit, FileText, LucideIcon, Users } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { showToast } from '@/helper/toast.helper';
import { getChangedValues } from '@/helper/utils';
import { usePermission } from '@/hooks/usePermission';
import { sitesService } from '@/services/siteManagementService/siteManagementService';
import { AppDispatch, RootState } from '@/store';
import { fetchMetaData } from '@/store/slices/metaDataSlice/metaDataSlice';
import { getSiteById } from '@/store/slices/siteManagementSlice/siteManagementSlice';
import { Project, SiteFormData } from '@/types/project.types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import tzlookup from 'tz-lookup';
import { Circle, MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import CurrencyDropdown from '../CommonComponents/CurrencyDropdown';
import CustomDropdown from '../CommonComponents/CustomDropdown';
import Loader from '../CommonComponents/Loader';
import MultiSelectDropdown from '../CommonComponents/MultiSelectDropdown';
import ProjectInfoCard from '../CommonComponents/ProjectInfoCard';
import DropdownWithRadio from '../CommonComponents/RadioButtons';
import StatCard from '../CommonComponents/StatCard';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Stat {
  id: number;
  label: string;
  value: number;
  icon: LucideIcon;
  iconBg: string;
}

interface SiteFormState {
  name: string;
  countryId: number;
  currency: string;
  maxUsers: number;
  languageIds: number[];
  centerLat: number;
  centerLng: number;
  radiusMeters: number;
  status: boolean;
  timezone: string;
}

type NavState = {
  siteId?: number;
  projectId?: number;
  project?: Project;
  from?: string;
};

const EditSite: React.FC = () => {
  const [formData, setFormData] = useState<SiteFormState>({
    name: '',
    countryId: 0,
    currency: '',
    maxUsers: 0,
    languageIds: [],
    centerLat: 0,
    centerLng: 0,
    radiusMeters: 0,
    status: true,
    timezone: '',
  });

  const [originalData, setOriginalData] = useState<SiteFormState>({
    name: '',
    countryId: 0,
    currency: '',
    maxUsers: 0,
    languageIds: [],
    centerLat: 0,
    centerLng: 0,
    radiusMeters: 0,
    status: true,
    timezone: '',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const navState = (location.state as NavState) || {};

  const siteId = navState.siteId;
  const from = navState.from;
  const project = navState.project;
  const projectId = navState.projectId;

  const { selected: site, loading } = useSelector((state: RootState) => state.sites);
  const { countries, languages } = useSelector((state: RootState) => state.metaData);
  const [nameError, setNameError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const projectFromSite = site?.project ?? null;
  const selectedProjectStats = site?.count;

  const stats: Stat[] = [
    {
      id: 1,
      label: 'Total Users',
      value: selectedProjectStats?.totalusers || 0,
      icon: Users,
      iconBg: 'bg-[#4C86E780]',
    },
    {
      id: 2,
      label: 'Total Survey Sets',
      value: selectedProjectStats?.totalSurveySets || 0,
      icon: FileText,
      iconBg: 'bg-[#E86A9B80]',
    },
    {
      id: 3,
      label: 'Active Surveys',
      // TODO: get active surveys
      value: 0,
      icon: FileText,
      iconBg: 'bg-[#5CC9A780]',
    },
    {
      id: 4,
      label: 'Notifications',
      value: selectedProjectStats?.notification || 0,
      icon: Bell,
      iconBg: 'bg-[#F4A12C80]',
    },
  ];

  const permissions = {
    write: usePermission('write', 'sites'),
  };

  useEffect(() => {
    if (site) {
      const newData: SiteFormState = {
        name: site.name ?? '',
        countryId: site.country?.id ?? 0,
        currency: site.currency ?? '',
        maxUsers: site.maxUsers ?? 0,
        languageIds: site.languages.map((lang) => lang.id ?? []),
        centerLat: site.geofence?.centerLat ?? 0,
        centerLng: site.geofence?.centerLng ?? 0,
        radiusMeters: site.geofence.radiusMeters ?? 0,
        status: site.status,
        timezone: (site.geofence as any)?.timezone ?? '',
      };

      setFormData(newData);
      setOriginalData(newData);
    }
  }, [site]);

  useEffect(() => {
    dispatch(fetchMetaData());
    if (siteId) {
      dispatch(getSiteById(siteId.toString()));
    }
  }, [siteId, dispatch]);

  const getValidationErrors = (): string[] => {
    const errors: string[] = [];

    if (!formData.name.trim()) {
      errors.push('Site name is required');
    } else if (formData.name.trim().length < 5 || formData.name.trim().length > 60) {
      errors.push('Site name must be 5–60 characters.');
    }

    if (!formData.countryId) {
      errors.push('Country is required');
    }

    if (formData.centerLat === 0) {
      errors.push('Latitude is required');
    }

    if (formData.centerLng === 0) {
      errors.push('Longitude is required');
    }

    if (formData.radiusMeters === 0) {
      errors.push('Radius is required');
    }

    if (formData.languageIds.length === 0) {
      errors.push('At least one language is required');
    }

    if (!formData.currency) {
      errors.push('Currency is required');
    }

    return errors;
  };

  const hasValidationErrors = getValidationErrors().length > 0;

  const handleSave = async () => {
    if (!siteId) return;

    const errors = getValidationErrors();
    if (errors.length > 0) return;

    setIsSaving(true);
    try {
      const changedFields = getChangedValues(originalData, formData);

      const updatePayload: Partial<SiteFormData> = {
        id: siteId,
        ...changedFields,

        languages: formData.languageIds,
      };

      if (
        changedFields.centerLat !== undefined ||
        changedFields.centerLng !== undefined ||
        changedFields.radiusMeters !== undefined
      ) {
        updatePayload.geofence = {
          centerLat: formData.centerLat,
          centerLng: formData.centerLng,
          radiusMeters: formData.radiusMeters,
          timezone: formData.timezone || '',
        };
      }

      await sitesService.updateSite(updatePayload);

      setOriginalData(formData);
      setIsEditing(false);
      showToast.success('Site updated successfully');
    } catch (error) {
      showToast.error(error as string);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (
    field: keyof SiteFormState,
    value: SiteFormState[keyof SiteFormState],
  ) => {
    if (field === 'name') {
      const nameValue = value as string;

      if (nameValue.length < 5 || nameValue.length > 60) {
        setNameError('Site name must be between 5 and 60 characters');
      } else {
        setNameError(null);
      }
    }

    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'centerLat' || field === 'centerLng') {
        const lat = field === 'centerLat' ? (value as number) : prev.centerLat;
        const lng = field === 'centerLng' ? (value as number) : prev.centerLng;
        if (lat && lng) {
          try {
            updated.timezone = tzlookup(lat, lng);
          } catch {}
        }
      }
      return updated;
    });
  };

  const LocationPicker: React.FC<{
    onLocationSelect: (lat: number, lng: number) => void;
  }> = ({ onLocationSelect }) => {
    const handleMapClick = useCallback(
      (e: any) => {
        const { lat, lng } = e.latlng;
        onLocationSelect(lat, lng);
      },
      [onLocationSelect],
    );

    useMapEvents({
      click: handleMapClick,
    });

    return null;
  };

  const handleLocationSelect = useCallback((lat: number, lng: number) => {
    let timezone = '';
    try {
      timezone = tzlookup(lat, lng);
    } catch {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    setFormData((prev) => ({
      ...prev,
      centerLat: lat,
      centerLng: lng,
      timezone,
    }));
  }, []);

  const handleDiscard = () => {
    setFormData(originalData);
    setIsEditing(false);
  };

  const displayData = isEditing ? formData : originalData;

  const activeColor = displayData.status ? 'bg-[#0BA96FCC]' : 'bg-[#E24A4ACC]';

  const selectedCountry = countries.find((c) => c.id === displayData.countryId);
  const selectedLanguages = languages.filter((l) => displayData.languageIds.includes(l.id));

  const title = from === 'project-dashboard' ? 'Project Management' : 'Site Management';
  const description =
    from === 'project-dashboard'
      ? 'Manage all projects you have access too'
      : 'View and monitor all your active sites.';
  const label = from === 'project-dashboard' ? 'project' : 'site';

  if (loading) {
    return (
      <PageHeaderWrapper
        title={title}
        description={description}
        onAdd={() => navigate(-1)}
        addLabel={`Return to ${label} dashboard`}
      >
        <Loader />
      </PageHeaderWrapper>
    );
  }

  return (
    <PageHeaderWrapper
      title={title}
      description={description}
      onAdd={() => navigate(-1)}
      addLabel={`Return to ${label} dashboard`}
    >
      {projectFromSite && (
        <ProjectInfoCard
          project={projectFromSite}
          showEdit={false}
          showExpand={false}
          showFile={false}
          showCollapse
          onCollapse={() => navigate(-1)}
          enableCollapse
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6 gap-x-3">
        {stats.map((stat, index) => (
          <StatCard
            key={stat.id}
            icon={stat.icon}
            iconBg={stat.iconBg}
            title={stat.label}
            value={stat.value}
          />
        ))}
      </div>

      {/* Site Details */}
      <div className="italic">
        <h3 className="text-[#4A082D] mb-1 ">Site Details</h3>

        <div className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* Site Name */}
            <div className="md:col-span-2 relative">
              <div className=" flex w-full p-2 px-3 border-[#4A082D] rounded-lg capitalize bg-[#4A082D]/10 text-md text-[#4A082D] focus:outline-none">
                <p className=" pr-2 pt-1 font-semibold">Site Name : </p>
                <input
                  type="text"
                  className={`${!isEditing ? ' cursor-not-allowed' : ''}
                  ${
                    isEditing &&
                    (displayData.name.trim() === '' ||
                      displayData.name.trim().length < 5 ||
                      displayData.name.trim().length > 60)
                      ? 'border-b-2 border-red-500 '
                      : 'border-b border-transparent'
                  } bg-transparent p-1 focus:outline-none `}
                  value={displayData.name}
                  readOnly={!isEditing}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Site Name"
                />
              </div>
              {isEditing && displayData.name.trim() === '' && (
                <span className="absolute left-[calc(0.5rem+8.3ch)] top-2 text-red-500 pointer-events-none">
                  *
                </span>
              )}
              {isEditing &&
                displayData.name.trim() !== '' &&
                (displayData.name.trim().length < 5 || displayData.name.trim().length > 60) && (
                  <div className="text-red-500 text-xs mt-1">
                    Site name must be 5–60 characters.
                  </div>
                )}
            </div>

            {/* Country */}

            <div className="md:col-span-2">
              <div className="flex items-center w-full gap-3 p-2 rounded-lg bg-[#4A082D]/10 text-md text-[#4A082D]">
                <label className="font-semibold whitespace-nowrap">Country :</label>

                <div className="relative flex-1">
                  {isEditing ? (
                    <>
                      <CustomDropdown
                        value={displayData.countryId}
                        onChange={(value) => handleInputChange('countryId', Number(value))}
                        options={countries.map((country) => ({
                          value: country.id,
                          label: country.name,
                        }))}
                        placeholder="Country"
                        required
                        requiredIndicatorPosition="left-[calc(0.5rem+7ch)]"
                        overrideClassName="flex w-full justify-between"
                      />
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="w-full bg-transparent cursor-not-allowed focus:outline-none text-md pr-8"
                        value={selectedCountry?.name || site?.country?.name || ''}
                        readOnly
                        placeholder="Country"
                      />

                      <ChevronDown
                        size={18}
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#4A082D] opacity-70"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Longitude */}
            <div className=" flex relative w-full p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D]/10 text-md text-[#4A082D] focus:outline-none">
              <label className="pr-2 font-semibold">Longitude :</label>
              <input
                type="number"
                step="0.0001"
                className={` bg-transparent ${
                  !isEditing ? 'cursor-not-allowed bg-opacity-60' : ''
                }`}
                value={displayData.centerLng === 0 ? '' : displayData.centerLng}
                readOnly={!isEditing}
                onChange={(e) =>
                  handleInputChange(
                    'centerLng',
                    e.target.value === '' ? 0 : parseFloat(e.target.value),
                  )
                }
                placeholder="Longitude"
              />
              {isEditing && displayData.centerLng === 0 && (
                <span className="absolute left-[calc(0.5rem+8ch)] top-2 text-red-500 pointer-events-none">
                  *
                </span>
              )}
            </div>

            {/* Latitude */}
            <div className="flex relative w-full p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D]/10 text-md text-[#4A082D] focus:outline-none">
              <label className="pr-2 font-semibold">Latitude : </label>
              <input
                type="number"
                step="0.0001"
                className={`bg-transparent ${!isEditing ? 'cursor-not-allowed bg-opacity-60' : ''}`}
                value={displayData.centerLat === 0 ? '' : displayData.centerLat}
                readOnly={!isEditing}
                onChange={(e) =>
                  handleInputChange(
                    'centerLat',
                    e.target.value === '' ? 0 : parseFloat(e.target.value),
                  )
                }
                placeholder="Latitude"
              />
              {isEditing && displayData.centerLat === 0 && (
                <span className="absolute left-[calc(0.5rem+7ch)] top-2 text-red-500 pointer-events-none">
                  *
                </span>
              )}
            </div>
          </div>

          {/* Formatted Coordinates Display */}
          {displayData.centerLat !== 0 && displayData.centerLng !== 0 && (
            <div className="text-sm text-[#621C43] bg-[#4A082D33] p-2 rounded-lg text-center md:col-span-2">
              Longitude - {Math.abs(displayData.centerLng).toFixed(4)}°{' '}
              {displayData.centerLng >= 0 ? 'E' : 'W'} and Latitude -{' '}
              {Math.abs(displayData.centerLat).toFixed(4)}° {displayData.centerLat >= 0 ? 'N' : 'S'}
            </div>
          )}

          {/* Radius */}
          <div className="flex relative  w-full p-2 px-3 pr-10 border-[#4A082D] rounded-lg bg-[#4A082D]/10 text-md text-[#4A082D] ">
            <label className="pr-2 font-semibold">Radius (meters) :</label>
            <input
              type="number"
              className={` bg-transparent ${!isEditing ? 'cursor-not-allowed bg-opacity-60' : ''}`}
              value={displayData.radiusMeters === 0 ? '' : displayData.radiusMeters}
              readOnly={!isEditing}
              onChange={(e) =>
                handleInputChange(
                  'radiusMeters',
                  e.target.value === '' ? 0 : Math.max(1, parseInt(e.target.value) || 0),
                )
              }
              placeholder="Radius (in meters)"
            />
            {isEditing && displayData.radiusMeters === 0 && (
              <span className="absolute left-[calc(0.5rem+15ch)] top-2 text-red-500 pointer-events-none">
                *
              </span>
            )}
          </div>

          {/* Languages */}
          <div className="flex relative w-full p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D]/10 text-md text-[#4A082D] focus:outline-none">
            <label className="font-semibold whitespace-nowrap pt-2">Languages :</label>

            <div className="relative flex-1 bg-transparent">
              {isEditing ? (
                <MultiSelectDropdown
                  options={languages.map((language) => ({
                    id: language.id,
                    name: language.name,
                  }))}
                  selectedIds={displayData.languageIds}
                  onChange={(selectedIds) => handleInputChange('languageIds', selectedIds)}
                  placeholder="Choose Language(s)"
                  disabled={false}
                  className="flex bg-transparent bg-[#4A082D] p-[4px]"
                />
              ) : (
                <div className="flex flex-wrap gap-1 cursor-not-allowed pt-2">
                  {selectedLanguages.length > 0 ? (
                    selectedLanguages.map((lang) => (
                      <span
                        key={lang.id}
                        className="inline-flex items-center bg-[#621C43] text-white px-2 py-1 rounded text-xs"
                      >
                        {lang.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-[#621C43]">Choose Language(s)</span>
                  )}
                </div>
              )}

              {!isEditing && (
                <ChevronDown
                  size={18}
                  className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#4A082D] opacity-70"
                />
              )}
            </div>
          </div>

          {isEditing && displayData.languageIds.length === 0 && (
            <span className="absolute left-[calc(0.5rem+15.5ch)] top-2 text-red-500 pointer-events-none">
              *
            </span>
          )}

          {/* Currency */}
          <div className="flex items-center w-full gap-3 p-2 rounded-lg bg-[#4A082D]/10 text-md text-[#4A082D]">
            <label className="font-semibold whitespace-nowrap">
              {isEditing ? 'Choose ' : ''}Reward Currency :
            </label>

            <div className="relative flex-1">
              <CurrencyDropdown
                value={displayData.currency}
                onChange={(value) => handleInputChange('currency', value)}
                placeholder="Choose Reward Currency"
                required={true}
                requiredIndicatorPosition="left-[calc(0.5rem+20ch)]"
                disabled={!isEditing}
                disableStyling={!isEditing}
                className="flex justify-between bg-transparent p-[2px] w-full"
              />

              {!isEditing && (
                <ChevronDown
                  size={18}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#4A082D] opacity-70"
                />
              )}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center p-2 px-3 border-[#4A082D] rounded-lg bg-[#4A082D]/10 justify-between text-[#4A082D]">
            <div className="flex items-center">
              <p className="pr-2 font-semibold">Status :</p>

              {isEditing ? (
                <div className="relative">
                  <DropdownWithRadio
                    label="status"
                    options={['Active', 'Inactive']}
                    selected={displayData.status ? 'Active' : 'Inactive'}
                    onChange={(value) => handleInputChange('status', value === 'Active')}
                    isOpen={statusDropdownOpen}
                    onOpen={() => setStatusDropdownOpen(true)}
                    onClose={() => setStatusDropdownOpen(false)}
                    placeholder="Select Status"
                  />
                </div>
              ) : (
                <span className={`px-3 py-1 text-sm font-semibold rounded-full  ${activeColor}`}>
                  {displayData.status ? 'Active' : 'Inactive'}
                </span>
              )}
            </div>
          </div>

          {/* Buttons */}
          {isEditing && (
            <div className="flex justify-end gap-4 mt-4">
              <button
                onClick={handleDiscard}
                disabled={isSaving}
                className="px-4 py-1 bg-gray-200 text-[#4A082D] rounded-lg hover:bg-gray-300 transition font-medium disabled:opacity-50"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || hasValidationErrors}
                className="px-4 py-1 bg-[#0BA96F] text-white rounded-lg hover:bg-[#0BA96F]/80 transition font-medium disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="relative mt-6 h-64 sm:h-80 w-full rounded-xl overflow-hidden shadow-lg border border-gray-200">
          <MapContainer
            center={[displayData.centerLat || 17.4065, displayData.centerLng || 78.4772]}
            zoom={5}
            scrollWheelZoom={true}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
            maxBoundsViscosity={1.0}
            className={`h-full w-full ${isEditing ? 'cursor-crosshair' : 'cursor-grab'}`}
          >
            <TileLayer noWrap={true} url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            {isEditing && <LocationPicker onLocationSelect={handleLocationSelect} />}

            {displayData.radiusMeters && displayData.radiusMeters > 0 && (
              <Circle
                center={[displayData.centerLat || 17.4065, displayData.centerLng || 78.4772]}
                radius={displayData.radiusMeters}
                pathOptions={{
                  fillColor: '#621C43',
                  fillOpacity: 0.2,
                  color: '#621C43',
                  weight: 2,
                }}
              />
            )}
            <Marker
              position={[displayData.centerLat || 17.4065, displayData.centerLng || 78.4772]}
              draggable={isEditing}
              eventHandlers={{
                dragend: (e) => {
                  if (isEditing) {
                    const marker = e.target;
                    const position = marker.getLatLng();
                    handleLocationSelect(position.lat, position.lng);
                  }
                },
              }}
            >
              <Popup>
                <strong>{site?.name}</strong>
                <br />
                {site?.country?.name}
                <br />
                Longitude - {Math.abs(displayData.centerLng).toFixed(4)}°{' '}
                {displayData.centerLng >= 0 ? 'E' : 'W'} and Latitude -{' '}
                {Math.abs(displayData.centerLat).toFixed(4)}°{' '}
                {displayData.centerLat >= 0 ? 'N' : 'S'}
                {displayData.radiusMeters > 0 && (
                  <>
                    <br />
                    Radius: {displayData.radiusMeters}m
                  </>
                )}
                {isEditing && (
                  <>
                    <br />
                    <em>Drag marker or click map to move location</em>
                  </>
                )}
              </Popup>
            </Marker>
          </MapContainer>

          {!isEditing && (
            <div className="absolute bottom-2 right-2 flex gap-2 z-[1000]">
              <button
                onClick={() => navigate(-1)}
                className="p-3 bg-white rounded-md shadow-md hover:bg-gray-100 transition duration-150"
                title="Go Back"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>

              <button
                disabled={!permissions.write}
                onClick={() => setIsEditing(true)}
                title="Edit Fields"
                className={`p-3 bg-white rounded-md shadow-md transition duration-150
    ${permissions.write ? 'hover:bg-gray-100 cursor-pointer' : 'opacity-50 cursor-not-allowed'}
  `}
              >
                <Edit className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          )}
        </div>
      </div>
    </PageHeaderWrapper>
  );
};

export default EditSite;
